import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  getSubmitButtonProps,
  RichTextEditor,
} from '@marageti/z4-lib';

const schema = z.object({
  text: z.string().min(1, 'Comment cannot be empty'),
});

type EditCommentFormData = z.infer<typeof schema>;

type TicketCommentEditModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (text: string) => Promise<void>;
  defaultText: string;
};

const TicketCommentEditModal = ({
  open,
  onClose,
  onSave,
  defaultText,
}: TicketCommentEditModalProps) => {
  const [editorKey, setEditorKey] = useState(0);

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit,
    reset,
  } = useForm<EditCommentFormData>({
    resolver: zodResolver(schema),
    defaultValues: { text: defaultText },
  });

  useEffect(() => {
    if (open) {
      reset({ text: defaultText });
      setEditorKey((prev) => prev + 1);
    }
  }, [open, defaultText, reset]);

  const onSubmit: SubmitHandler<EditCommentFormData> = async (data) => {
    await onSave(data.text);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Comment</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Controller
          control={control}
          name="text"
          render={({ field }) => (
            <RichTextEditor
              key={editorKey}
              editorName="edit-comment-editor"
              defaultValue={defaultText}
              label="Comment"
              onChange={field.onChange}
              useLinks
              useLists
              sx={{
                minHeight: '150px',
                maxHeight: '300px',
                flex: 1,
                mb: 0,
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Update
        </LoadingButton>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketCommentEditModal;
